import {
  ExceptionFilter,
  Catch,
  ArgumentsHost,
  HttpStatus,
  Logger,
} from '@nestjs/common';
import { FastifyRequest, FastifyReply } from 'fastify';
import {
  UniqueConstraintViolationException,
  NotNullConstraintViolationException,
  ForeignKeyConstraintViolationException,
  CheckConstraintViolationException,
  InvalidFieldNameException,
  ValidationError,
  NotFoundError,
  OptimisticLockError,
  ConnectionException,
  DriverException,
} from '@mikro-orm/core';

function isError(value: unknown): value is Error {
  return value instanceof Error;
}

function hasProperty<T extends string>(
  obj: unknown,
  prop: T,
): obj is Record<T, unknown> {
  return typeof obj === 'object' && obj !== null && prop in obj;
}

interface ErrorResponse {
  success: false;
  statusCode: number;
  message: string;
  errorType: string;
  errors?: Record<string, unknown>;
}

@Catch(
  UniqueConstraintViolationException,
  NotNullConstraintViolationException,
  ForeignKeyConstraintViolationException,
  CheckConstraintViolationException,
  InvalidFieldNameException,
  ValidationError,
  NotFoundError,
  OptimisticLockError,
  ConnectionException,
  DriverException,
)
export class MikroOrmExceptionFilter implements ExceptionFilter {
  private readonly logger = new Logger(MikroOrmExceptionFilter.name);

  catch(exception: unknown, host: ArgumentsHost) {
    const ctx = host.switchToHttp();
    const response = ctx.getResponse<FastifyReply>();
    const request = ctx.getRequest<FastifyRequest>();

    const errorResponse = this.handleMikroOrmException(exception);

    const errorMessage = isError(exception)
      ? exception.message
      : 'Unknown error';
    const errorStack = isError(exception) ? exception.stack : undefined;

    this.logger.error(
      `MikroORM Error: ${errorMessage}`,
      errorStack,
      `${request.method} ${request.url}`,
    );

    response.status(errorResponse.statusCode).send(errorResponse);
  }

  private handleMikroOrmException(
    exception: unknown,
  ): Omit<ErrorResponse, 'timestamp' | 'path'> {
    if (exception instanceof UniqueConstraintViolationException) {
      return {
        success: false,
        statusCode: HttpStatus.CONFLICT,
        message: this.extractUniqueConstraintMessage(exception),
        errorType: 'Unique Constraint Violation',
        errors: this.extractConstraintDetails(exception),
      };
    }

    if (exception instanceof NotNullConstraintViolationException) {
      const columnName = this.extractColumnName(exception);
      return {
        success: false,
        statusCode: HttpStatus.BAD_REQUEST,
        message: `Required field${columnName ? ` '${columnName}'` : ''} cannot be null`,
        errorType: 'Not Null Constraint Violation',
        errors: this.extractConstraintDetails(exception),
      };
    }

    if (exception instanceof ForeignKeyConstraintViolationException) {
      return {
        success: false,
        statusCode: HttpStatus.BAD_REQUEST,
        message: 'Foreign key constraint violation',
        errorType: 'Foreign Key Constraint Violation',
        errors: this.extractConstraintDetails(exception),
      };
    }

    if (exception instanceof CheckConstraintViolationException) {
      return {
        success: false,
        statusCode: HttpStatus.BAD_REQUEST,
        message: 'Check constraint violation',
        errorType: 'Check Constraint Violation',
        errors: this.extractConstraintDetails(exception),
      };
    }

    if (exception instanceof InvalidFieldNameException) {
      const message = isError(exception)
        ? exception.message
        : 'Invalid field name';
      return {
        success: false,
        statusCode: HttpStatus.BAD_REQUEST,
        message: `Invalid field name: ${message}`,
        errorType: 'Invalid Field Name',
      };
    }

    if (exception instanceof ValidationError) {
      return {
        success: false,
        statusCode: HttpStatus.BAD_REQUEST,
        message: 'Validation failed',
        errorType: 'Validation Error',
        errors: this.extractValidationErrors(exception),
      };
    }

    if (exception instanceof NotFoundError) {
      return {
        success: false,
        statusCode: HttpStatus.NOT_FOUND,
        message: 'Entity not found',
        errorType: 'Not Found',
      };
    }

    if (exception instanceof OptimisticLockError) {
      return {
        success: false,
        statusCode: HttpStatus.CONFLICT,
        message: 'Entity was modified by another process',
        errorType: 'Optimistic Lock Error',
        errors: this.extractOptimisticLockDetails(exception),
      };
    }

    if (exception instanceof ConnectionException) {
      return {
        success: false,
        statusCode: HttpStatus.SERVICE_UNAVAILABLE,
        message: 'Database connection error',
        errorType: 'Connection Error',
      };
    }

    if (exception instanceof DriverException) {
      return {
        success: false,
        statusCode: HttpStatus.INTERNAL_SERVER_ERROR,
        message: 'Database driver error',
        errorType: 'Driver Error',
      };
    }

    return {
      success: false,
      statusCode: HttpStatus.INTERNAL_SERVER_ERROR,
      message: 'An unexpected database error occurred',
      errorType: 'Database Error',
    };
  }

  private extractUniqueConstraintMessage(
    exception: UniqueConstraintViolationException,
  ): string {
    const message = isError(exception) ? exception.message : '';

    const patterns = [
      /duplicate key value violates unique constraint "(\w+)"/i,
      /unique constraint failed: (\w+)\.(\w+)/i,
      /duplicate entry .* for key '(\w+)'/i,
      /constraint "(\w+)" violated/i,
    ];

    for (const pattern of patterns) {
      const match = message.match(pattern);
      if (match) {
        const fieldName = match[2] || match[1];
        if (fieldName && fieldName !== 'PRIMARY') {
          return `${fieldName} must be unique`;
        }
      }
    }

    return 'Duplicate entry detected';
  }

  private extractColumnName(exception: unknown): string | null {
    if (!isError(exception)) return null;

    const message = exception.message;
    const patterns = [
      /column "(\w+)" cannot be null/i,
      /null value in column "(\w+)"/i,
      /(\w+) cannot be null/i,
    ];

    for (const pattern of patterns) {
      const match = message.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }

    return null;
  }

  private extractConstraintDetails(
    exception: unknown,
  ): Record<string, unknown> {
    const details: Record<string, unknown> = {};

    if (hasProperty(exception, 'constraint')) {
      details.constraint = exception.constraint;
    }

    if (hasProperty(exception, 'table')) {
      details.table = exception.table;
    }

    if (hasProperty(exception, 'column')) {
      details.column = exception.column;
    }

    return details;
  }

  private extractOptimisticLockDetails(
    exception: OptimisticLockError<any>,
  ): Record<string, unknown> {
    const details: Record<string, unknown> = {};

    if (hasProperty(exception, 'entity')) {
      details.entity = exception.entity;
    }

    if (hasProperty(exception, 'expectedVersion')) {
      details.expectedVersion = exception.expectedVersion;
    }

    if (hasProperty(exception, 'actualVersion')) {
      details.actualVersion = exception.actualVersion;
    }

    return details;
  }

  private extractValidationErrors(
    exception: ValidationError<any>,
  ): Record<string, unknown> | undefined {
    if (!isError(exception)) return;

    const message = exception.message;
    if (message) {
      try {
        return JSON.parse(message) as Record<string, unknown>;
      } catch {
        return { message };
      }
    }
  }
}
